import React, {useEffect, useState} from 'react';
import Table from '../../../common/table/Table';
import {adminContractTableAPI} from "../../../../services/admin";
import {GetAdminContractTableResponseData} from "../../../../types/admin";
import {Column} from "../../../common/table/TableSection";
import DateRangeFilter from '../../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "../../../client/document/ClientDocumentTable.module.css";
import {ClientSort, ClientTarget, ContractStatus} from "../../../../types/contract";
import CreateContractPopUp from "../../../../views/admin/popup/CreateContractPopUp";
import Button from "../../../common/button/Button";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    start: DateRange;
    expire: DateRange;
}

//어드민 계약 테이블
const AdminContractTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<GetAdminContractTableResponseData[]>([]);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<ClientTarget>(ClientTarget.ALL);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [openFilter, setOpenFilter] = useState<string | null>(null);
    const [filters, setFilters] = useState<Record<string, any>>({
        contractStatus: ['계약', '만료', '예정'],
        start: 'ALL',
        expire: 'ALL'
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        start: {start: null, end: null},
        expire: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    // 리셋 상태 추가
    const [isResetting, setIsResetting] = useState(false);

    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            contractStatus: ['계약', '만료', '예정'],
            start: 'ALL',
            expire: 'ALL'
        });

        // 날짜 범위 초기화
        setDateRanges({
            start: { start: null, end: null },
            expire: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');
        setSearchType(ClientTarget.ALL);

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 선택된 행 초기화
        setSelectedRows(new Set());

        // 열린 필터 닫기
        setOpenFilter(null);

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    useEffect(() => {
        //로우별 체크박스 선택 가능
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        const headerCheckbox = checkboxes[0] as HTMLInputElement;
        if (headerCheckbox) {
            headerCheckbox.indeterminate = selectedRows.size > 0 && selectedRows.size < data.length;
        }
    }, [selectedRows, data]);

    //필터창 토글
    const handleFilterToggle = (filterKey: string) => {
        setOpenFilter(current => current === filterKey ? null : filterKey);
    };

    //체크박스 전체 선택
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = data.map(row => row.contractId);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    //체크박스 단일 선택
    const handleSelectRow = (clientId: number, checked: boolean) => {
        const newSelected = new Set(selectedRows);
        if (checked) {
            newSelected.add(clientId);
        } else {
            newSelected.delete(clientId);
        }
        setSelectedRows(newSelected);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: GetAdminContractTableResponseData) => {
        //세부 페이지로 이동
        navigate(`/admin/clients/contract/${row.contractId}`);
    };

    //계약 상태 필터
    const statusFilterOptions = [
        {label: '계약', value: '계약'},
        {label: '만료', value: '만료'},
        {label: '예정', value: '예정'}
    ];

    //컬럼
    const columns: Column<GetAdminContractTableResponseData>[] = [
        // 체크 박스
        {
            key: 'checkbox',
            header: (
                <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.size === data.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={styles.checkbox}
                />
            ),
            width: '40px',
            render: (_, row) => (
                <input
                    type="checkbox"
                    checked={selectedRows.has(row.contractId)}
                    onChange={(e) => handleSelectRow(row.contractId, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.checkbox}
                />
            )
        },
        {
            key: 'companyName',
            header: '회사명',
            width: '20%',
            sortable: true
        },
        {
            key: 'businessNumber',
            header: '사업자등록번호',
            width: '20%'
        },
        {
            key: 'start',
            header: '계약일',
            width: '20%',
            sortable: true
        },
        {
            key: 'expire',
            header: '만료일',
            width: '20%',
            sortable: true
        },
        {
            key: 'status',
            header: '계약 상태',
            width: '20%'
        }
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        // 리셋 중일 때는 날짜 변경 로직 건너뛰기
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'contractStatus',
            label: '계약 상태',
            options: statusFilterOptions,
            multiple: true,
            value: filters.contractStatus,
            component: undefined
        },
        {
            key: 'start',
            label: '계약일',
            component: (
                <DateRangeFilter
                    label="계약일"
                    startDate={dateRanges.start.start}
                    endDate={dateRanges.start.end}
                    onDateChange={(start, end) => handleDateRangeChange('start', start, end)}
                    isOpen={openFilter === 'start'}
                    onToggle={() => handleFilterToggle('start')}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'expire',
            label: '만료일',
            component: (
                <DateRangeFilter
                    label="만료일"
                    startDate={dateRanges.expire.start}
                    endDate={dateRanges.expire.end}
                    onDateChange={(start, end) => handleDateRangeChange('expire', start, end)}
                    isOpen={openFilter === 'expire'}
                    onToggle={() => handleFilterToggle('expire')}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): ClientSort => {
        if (!field || !direction) return ClientSort.START_DESC;
        const sortMap: Record<string, Record<string, ClientSort>> = {
            companyName: {
                asc: ClientSort.COMPANY_ASC,
                desc: ClientSort.COMPANY_DESC
            },
            start: {
                asc: ClientSort.START_ASC,
                desc: ClientSort.START_DESC
            },
            expire: {
                asc: ClientSort.EXPIRE_ASC,
                desc: ClientSort.EXPIRE_DESC
            }
        };
        return sortMap[field]?.[direction] || ClientSort.START_DESC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //테이블 데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //결제 데이터 API 호출
            const response = await adminContractTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                search: searchTerm,
                target: searchType,
                contractStatus: filters.contractStatus as ContractStatus[],
                start: filters.start,
                expire: filters.expire
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, searchType, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: ClientTarget.ALL, label: '전체'},
        {value: ClientTarget.COMPANY_NAME, label: '회사명'},
        {value: ClientTarget.BUSINESS_NUMBER, label: '사업자등록번호'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //상태 필터 변경
            if (key === 'contractStatus') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return {...prev, [key]: ['계약', '만료', '예정']};
                }
                return {...prev, contractStatus: newStatuses};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>계약</h3>
                    <div className={styles.mobileDeleteButtons}>
                        <Button
                            onClick={() => setIsCreatePopupOpen(true)}
                            size={"small"}
                        >
                            추가
                        </Button>
                    </div>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value as ClientTarget)}
                            className={styles.searchTypeSelect}
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                    </div>
                    {/*계약 추가 버튼*/}
                    <Button
                        onClick={() => setIsCreatePopupOpen(true)}
                        size={"small"}
                        className={styles.deleteButton}
                    >
                        추가
                    </Button>
                </div>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
            {/*계약 추가 팝업*/}
            <CreateContractPopUp
                isOpen={isCreatePopupOpen}
                onClose={() => {
                    setIsCreatePopupOpen(false);
                    fetchData();
                }}
            />
        </div>
    );
};

export default AdminContractTable;